import {
    forwardRef,
    useMemo,
    type CSSProperties,
    type ForwardRefExoticComponent,
    type MouseEvent,
    type ReactNode,
    type RefAttributes,
} from 'react';
import classNames from 'classnames';
import styles from './FJButton.module.less';
import FJLocalStore from '../../../store/FJLocalStore';
import FJAutoLoading from '../FJLoading/FJAutoLoading';

export const FJButtonType = {
    DEFAULT: 'default',
    GRADIENT: 'gradient',
    LIGHT_GRADIENT: 'light-gradient',
    WHITE: 'white',
    ORANGE: 'orange',
    CREDIT: 'credit',
    DEFAULT_GRADIENT: 'default-gradient',
    RED_BLUE_GRADIENT: 'red-blue-gradient',
    WARNING: 'warning',
} as const;

type FJButtonTypeValue = (typeof FJButtonType)[keyof typeof FJButtonType];

interface FJButtonProps {
    className?: string;
    /** 文案对应 `FJLocalStore` 的 i18n 键名 */
    text?: string;
    isLoading?: boolean;
    type?: FJButtonTypeValue;
    disabled?: boolean;
    textClassName?: string;
    style?: CSSProperties;
    loadingStyle?: CSSProperties;
    icon?: ReactNode;
    rightNode?: ReactNode;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

const FJButton = forwardRef<HTMLDivElement, FJButtonProps>(function FJButton(
    {
        className,
        text,
        isLoading,
        type = FJButtonType.DEFAULT,
        disabled,
        textClassName,
        style,
        loadingStyle,
        icon,
        rightNode,
        onClick = () => {},
    },
    ref,
) {
    const cn = useMemo(() => {
        switch (type) {
            case FJButtonType.DEFAULT:
                return styles.default;
            case FJButtonType.GRADIENT:
                return styles.gradient;
            case FJButtonType.LIGHT_GRADIENT:
                return styles.light_gradient;
            case FJButtonType.WHITE:
                return styles.white;
            case FJButtonType.ORANGE:
                return styles.orange;
            case FJButtonType.CREDIT:
                return styles.credit;
            case FJButtonType.DEFAULT_GRADIENT:
                return styles.default_gradient;
            case FJButtonType.RED_BLUE_GRADIENT:
                return styles.red_blue_gradient;
            case FJButtonType.WARNING:
                return styles.warning;
            default:
                return styles.default;
        }
    }, [type]);

    const _onClick = (e: MouseEvent<HTMLDivElement>) => {
        if (isLoading || disabled) {
            return;
        }
        onClick(e);
    };

    return (
        <div
            ref={ref}
            className={classNames(cn, { [styles.disabled]: disabled }, className)}
            style={style}
            onClick={_onClick}
        >
            <div className={styles.btn_mask}></div>
            <div className={classNames(styles.btn, { [styles.opacity_0]: isLoading })}>
                {icon}
                {text ? <span className={classNames(textClassName)}>{FJLocalStore._(text)}</span> : null}
                {rightNode}
            </div>
            {isLoading ? (
                <FJAutoLoading
                    className={styles.loading}
                    shown={true}
                    innerStyle={loadingStyle}
                    strokeColor={type === FJButtonType.DEFAULT || type === FJButtonType.WHITE ? '#686c7e' : undefined}
                />
            ) : null}
        </div>
    );
});

const FJButtonWithTypes = FJButton as ForwardRefExoticComponent<FJButtonProps & RefAttributes<HTMLDivElement>>;

export default FJButtonWithTypes;
